import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { callFunction } from "../api/callFunction";
import VendorAPI from "../api/VendorApi";
import { db } from "../firebase";
import { fetchUsersByDocIds } from "./utils";
import { variantURL, fallbackToOriginal } from "../utils/imageVariant";
import { showToast } from "../utils/showToast";
import { GlobalContext } from "../context/GlobalContext";
import { timeAgo } from "./outreachFormat";

const SEARCH_RESULTS = 20;

function Avatar({ name, photoURL, size = "w-10 h-10" }) {
  return photoURL ? (
    <img
      src={variantURL(photoURL, 200)}
      onError={fallbackToOriginal(photoURL)}
      alt={name || "Host"}
      loading="lazy"
      className={`${size} rounded-full object-cover shrink-0`}
    />
  ) : (
    <div className={`${size} rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0`}>
      <span className="text-gray-500 dark:text-gray-400">{(name || "?").charAt(0).toUpperCase()}</span>
    </div>
  );
}

/**
 * The hosts an operator can still add: every vendors doc with a users doc
 * behind it, minus super admins (they have the full tab already) and anyone
 * who has access now. Hosts whose Stripe account is active come first (the
 * webhook writes stripeAccountStatus "active" once they can be paid, so these
 * are the hosts actually running events), then named before unnamed, then A
 * to Z. Pure, so it can be tested.
 *
 * `brand` is the vendor doc's own name ("SJ Group") when it differs from the
 * person's: operators often know a host by the brand, and searching only the
 * person's name hid them. It is searched, never shown: the row reads as the
 * person.
 */
export function hostCandidates(vendors, users, grantedIDs = new Set()) {
  const userById = new Map((users || []).map((u) => [u.id, u]));
  return (vendors || [])
    .filter((v) => userById.has(v.id))
    .filter((v) => v.data?.isSuperAdmin !== true)
    .filter((v) => v.data?.popularGuests?.enabled !== true && !grantedIDs.has(v.id))
    .map((v) => {
      const name = (userById.get(v.id).name || "").trim();
      const brand = typeof v.data?.name === "string" ? v.data.name.trim() : "";
      return {
        hostID: v.id,
        name,
        brand: brand && brand.toLowerCase() !== name.toLowerCase() ? brand : "",
        photoURL: userById.get(v.id).photoURL || "",
        stripeActive: v.data?.stripeAccountStatus === "active",
      };
    })
    .sort((a, b) => (b.stripeActive - a.stripeActive) || (!a.name - !b.name) || a.name.localeCompare(b.name));
}

/**
 * The candidates a search shows: a match on the person's name, the brand, or
 * the exact account id (pasted from somewhere else in the panel), capped at
 * `limit`. `total` is how many matched before the cap, so the dialog can say
 * the list is cut off rather than let someone scroll to the end and conclude
 * a host is missing.
 */
export function matchHosts(candidates, search, limit = SEARCH_RESULTS) {
  const raw = (search || "").trim();
  const q = raw.toLowerCase();
  const all = candidates || [];
  const hits = q
    ? all.filter((h) => h.hostID === raw || h.name.toLowerCase().includes(q) || h.brand.toLowerCase().includes(q))
    : all;
  return { matches: hits.slice(0, limit), total: hits.length };
}

// The same two reads the Suggestions host picker makes: every vendors doc, then
// only those hosts' own users docs, for a name and a face. Only run once an
// operator opens "Add host".
async function loadHostCandidates(grantedIDs) {
  const vendors = await VendorAPI.listVendorDocs();
  const users = await fetchUsersByDocIds(db, vendors.map((v) => v.id));
  return hostCandidates(vendors, users, grantedIDs);
}

function AddHostDialog({ grantedIDs, isSaving, onCancel, onConfirm }) {
  const [candidates, setCandidates] = useState(null); // null until loaded
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(null);
  const [notify, setNotify] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadHostCandidates(grantedIDs)
      .then((list) => { if (!cancelled) setCandidates(list); })
      .catch((err) => {
        console.error("[PopularHostAccess] host list failed:", err);
        if (!cancelled) {
          setCandidates([]);
          setLoadError("Could not load the host list.");
        }
      });
    return () => { cancelled = true; };
    // Once per open in practice: the dialog mounts fresh each time, and the
    // granted list does not move while it is up.
  }, [grantedIDs]);

  const { matches, total } = useMemo(() => matchHosts(candidates, search), [candidates, search]);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 sm:p-4" role="dialog" aria-modal="true" aria-label="Add a host">
      <div className="w-full sm:max-w-md max-h-[92dvh] overflow-y-auto rounded-t-2xl sm:rounded-lg bg-white dark:bg-gray-900 p-4 sm:p-5 shadow-xl">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Add a host</h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          They see the Popular tab on their own events the next time they open the panel.
        </p>

        <label htmlFor="popular-host-search" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mt-4 mb-1">
          Find a host
        </label>
        <input
          id="popular-host-search"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={isSaving}
          placeholder="Name, brand or account ID"
          autoComplete="off"
          className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
        />

        <div className="mt-2 flex flex-col gap-1 max-h-72 overflow-y-auto">
          {candidates === null && (
            <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">Loading hosts...</p>
          )}
          {loadError && (
            <p className="py-2 text-sm text-amber-700 dark:text-amber-400">{loadError}</p>
          )}
          {candidates !== null && !loadError && matches.length === 0 && (
            <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
              {search.trim() ? "No host matches that." : "Every host already has access."}
            </p>
          )}
          {total > matches.length && (
            <p className="pb-1 text-xs text-gray-500 dark:text-gray-400">
              Showing {matches.length} of {total}. Type a name to find the rest.
            </p>
          )}
          {matches.map((h) => (
            <button
              key={h.hostID}
              type="button"
              onClick={() => setPicked(h)}
              aria-pressed={picked?.hostID === h.hostID}
              disabled={isSaving}
              className={`flex items-center gap-3 p-2 rounded-md text-left text-sm transition-colors disabled:opacity-50 ${picked?.hostID === h.hostID
                ? "bg-primary/10 ring-1 ring-primary text-gray-900 dark:text-gray-100"
                : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                }`}
            >
              <Avatar name={h.name} photoURL={h.photoURL} size="w-8 h-8" />
              <span className="truncate">{h.name || "Unnamed host"}</span>
            </button>
          ))}
        </div>

        <label className="flex items-center gap-2 mt-4 text-sm text-gray-700 dark:text-gray-200">
          <input
            type="checkbox"
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
            disabled={isSaving}
            className="rounded border-gray-300 dark:border-gray-600"
          />
          Text them about it
        </label>

        <div className="flex gap-2 mt-4">
          <button
            type="button"
            onClick={() => picked && onConfirm({ host: picked, notify })}
            disabled={isSaving || !picked}
            className="flex-1 sm:flex-none px-4 py-2 rounded-md font-medium bg-primary text-white hover:bg-primaryDark transition-colors disabled:opacity-50"
          >
            {isSaving ? "Adding..." : picked ? `Add ${picked.name || "host"}` : "Add"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="flex-1 sm:flex-none px-4 py-2 rounded-md font-medium bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function HostAccessBox() {
  const [hosts, setHosts] = useState(null); // null until loaded
  const [loadError, setLoadError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [removing, setRemoving] = useState(null); // hostID

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const data = await callFunction("adminListPopularGuestsAccess", {}, { timeout: 60000 });
      setHosts(Array.isArray(data?.hosts) ? data.hosts : []);
    } catch (err) {
      console.error("adminListPopularGuestsAccess failed:", err);
      setLoadError(err?.message || "Could not load the hosts with access.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const grantedIDs = useMemo(() => new Set((hosts || []).map((h) => h.hostID)), [hosts]);

  const grant = async ({ host, notify }) => {
    setIsSaving(true);
    try {
      const data = await callFunction(
        "adminSetPopularGuestsAccess",
        { hostID: host.hostID, enabled: true, notify },
        { timeout: 60000 }
      );
      // A grant always lands; a text that did not go out is the one thing
      // worth a second look, so it is the one that is not green.
      const textMissed = notify && data?.notified?.attempted && !data?.notified?.sent;
      showToast(`${host.name || "Host"}: ${data?.message || "Access on"}`, { type: textMissed ? "warning" : "success" });
      setAdding(false);
      await load();
    } catch (err) {
      console.error("adminSetPopularGuestsAccess failed:", err);
      showToast(err?.message || "Could not give them access", { type: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const revoke = async (host) => {
    const name = host.name || "this host";
    if (!window.confirm(`Remove ${name}'s access to Popular guests? They stop seeing this tab right away.`)) return;
    setRemoving(host.hostID);
    try {
      const data = await callFunction(
        "adminSetPopularGuestsAccess",
        { hostID: host.hostID, enabled: false },
        { timeout: 60000 }
      );
      setHosts((prev) => (prev || []).filter((h) => h.hostID !== host.hostID));
      showToast(`${host.name || "Host"}: ${data?.message || "Access off"}`, { type: "success" });
    } catch (err) {
      console.error("adminSetPopularGuestsAccess failed:", err);
      showToast(err?.message || "Could not remove their access", { type: "error" });
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="mb-4 p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Hosts with access{hosts ? `: ${hosts.length}` : ""}
        </span>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="text-sm underline text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 shrink-0"
        >
          {expanded ? "Done" : "Manage"}
        </button>
      </div>

      {expanded && (
        <div className="mt-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Hosts you add here get this tab on their own events. They see the ranking and can reach
            out: invite, coupon, earn nudge. They never see the budget, booked guests, payouts or selfie
            checks, and they cannot book or pay anyone.
          </p>

          {loadError && (
            <div className="mt-3 p-3 rounded-md bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 text-sm">
              {loadError}{" "}
              <button type="button" onClick={load} className="underline">Try again</button>
            </div>
          )}

          {isLoading && hosts === null && (
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading...</p>
          )}

          {hosts && hosts.length === 0 && (
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No hosts have access yet.</p>
          )}

          {hosts && hosts.length > 0 && (
            <ul className="mt-3 flex flex-col gap-2">
              {hosts.map((h) => {
                const when = timeAgo(h.grantedAt);
                const added = ["Added", when, h.grantedByName ? `by ${h.grantedByName}` : ""].filter(Boolean).join(" ");
                return (
                  <li key={h.hostID} className="flex items-center gap-3">
                    <Avatar name={h.name} photoURL={h.photoURL} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {h.name || "Unnamed host"}
                        </span>
                        {h.notifiedAt && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-300">
                            Texted
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{added}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => revoke(h)}
                      disabled={Boolean(removing)}
                      className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 shrink-0"
                    >
                      {removing === h.hostID ? "Removing..." : "Remove"}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <button
            type="button"
            onClick={() => setAdding(true)}
            disabled={hosts === null}
            className="mt-3 w-full sm:w-auto px-4 py-2 rounded-md font-medium bg-primary text-white hover:bg-primaryDark transition-colors disabled:opacity-50"
          >
            Add host
          </button>
        </div>
      )}

      {adding && (
        <AddHostDialog
          grantedIDs={grantedIDs}
          isSaving={isSaving}
          onCancel={() => setAdding(false)}
          onConfirm={grant}
        />
      )}
    </div>
  );
}

/**
 * "Hosts with access": which hosts an operator has handed the Popular tab to,
 * at the top of the operator tab.
 *
 * Granting is CLAIM-ONLY server-side (it is an access change, the same tier as
 * payout settings), so a flag-only operator never sees this box at all rather
 * than a button that always fails.
 */
export function PopularHostAccess() {
  const { globalState } = useContext(GlobalContext);
  if (!globalState?.hasSuperAdminClaim) return null;
  return <HostAccessBox />;
}

export default PopularHostAccess;
